import {
  TextField,
  TextFieldProps as MuiTextFieldProps,
  IconButton,
  Card,
  Typography,
  CircularProgress,
} from '@mui/material'
import { Clear, FileUploadOutlined } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { useRef } from 'react'

type TextFieldProps = Omit<
  MuiTextFieldProps,
  'onChange' | 'select' | 'type' | 'multiline' | 'defaultValue'
>

type FileInputProps = TextFieldProps & {
  value?: File | null
  onChange?: (value: File | null) => void
  loading?: boolean
}

const HiddenInput = styled(TextField)({
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

const Container = styled('label')({
  height: 60,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 16,
  borderColor: 'rgba(0, 0, 0, 0.12)',
  cursor: 'pointer',
})

export function FileInput({
  value,
  onChange,
  placeholder = 'Selecionar arquivo',
  loading,
  ...props
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const resetInputValue = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    const file = fileList ? fileList[0] : null

    onChange?.(file)
  }

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onChange?.(null)
    resetInputValue()
  }

  const hasFile = Boolean(value)

  if (loading) {
    return (
      <Card component={Container} variant="outlined" sx={{ cursor: 'default' }}>
        <CircularProgress size={18} />
      </Card>
    )
  }

  return (
    <Card component={Container} htmlFor={props.id} variant="outlined">
      {!hasFile && <FileUploadOutlined />}

      <Typography noWrap>{value?.name || placeholder}</Typography>

      {hasFile && (
        <IconButton size="small" onClick={handleClear}>
          <Clear fontSize="small" />
        </IconButton>
      )}

      <HiddenInput
        inputRef={inputRef}
        type="file"
        onChange={handleChange}
        {...props}
      />
    </Card>
  )
}
